/**
 * The site's own confirmation popup, replacing window.confirm().
 *
 * Called imperatively, the same way window.confirm was, so call sites stay one line:
 *
 *   if (!(await confirmDialog({ title: "Delete test?", message: "…", danger: true }))) return;
 *
 * One <DialogHost /> is mounted at the root (main.tsx) and renders whichever dialog is open. Esc or a
 * click on the backdrop cancels; Enter confirms. Focus goes to the confirm button on open and back to
 * whatever had it on close.
 */

import { useEffect, useRef, useState, useSyncExternalStore } from "react";

export interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Red confirm button, for deletes and other things that cannot be undone. */
  danger?: boolean;
}

interface OpenDialog extends ConfirmOptions {
  resolve: (ok: boolean) => void;
}

let current: OpenDialog | null = null;
const listeners = new Set<() => void>();
const emit = (): void => listeners.forEach((listener) => listener());

export function confirmDialog(options: ConfirmOptions): Promise<boolean> {
  // A second dialog while one is open cancels the first rather than stacking.
  current?.resolve(false);
  return new Promise((resolve) => {
    current = { ...options, resolve };
    emit();
  });
}

function settle(ok: boolean): void {
  if (!current) return;
  const { resolve } = current;
  current = null;
  emit();
  resolve(ok);
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** How long the close animation runs before the dialog unmounts. Matches .dialog-*-out in CSS. */
const CLOSE_MS = 140;

export function DialogHost(): JSX.Element | null {
  const open = useSyncExternalStore(subscribe, () => current);
  // Keep the last dialog rendered briefly after it closes, so it can animate out.
  const [shown, setShown] = useState<OpenDialog | null>(null);
  const [closing, setClosing] = useState(false);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const returnFocus = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (open) {
      returnFocus.current = document.activeElement as HTMLElement | null;
      setShown(open);
      setClosing(false);
      return;
    }
    if (!shown) return;
    setClosing(true);
    const timer = setTimeout(() => {
      setShown(null);
      setClosing(false);
      returnFocus.current?.focus?.();
    }, CLOSE_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus();
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        settle(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!shown) return null;

  return (
    <div className="dialog-backdrop" data-closing={closing} onMouseDown={() => settle(false)}>
      <div
        className="dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        aria-describedby={shown.message ? "dialog-message" : undefined}
        data-danger={shown.danger === true}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="dialog-icon" aria-hidden="true">
          {shown.danger ? "!" : "?"}
        </div>
        <h2 id="dialog-title">{shown.title}</h2>
        {shown.message && <p id="dialog-message">{shown.message}</p>}
        <div className="dialog-buttons">
          <button type="button" className="btn" onClick={() => settle(false)}>
            {shown.cancelLabel ?? "Cancel"}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className="btn"
            data-variant={shown.danger ? "danger" : "primary"}
            onClick={() => settle(true)}
          >
            {shown.confirmLabel ?? "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
